import {ReactElement, Suspense, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {ToastContainer, toast, Bounce} from 'react-toastify'
import classNames from 'classnames'
import {useSelector} from 'react-redux'

import {
  LeftNavigation,
  MainLayout,
  TopNavigation,
  TrialFooter,
  MainContainer,
  RenderIf,
  PageFooter,
} from 'components'
import {
  usePolicy,
  useNativeInfo,
  useActions,
  useCommonFooter,
  useFeatureFlag,
} from 'hooks'
import {AuthProviderEnum, OIDCRouteEnum, PolicyRoutesEnum} from 'interfaces'
import {selectUser} from 'features/User'
import {SnapshotProvider} from 'context/SnapshotContext'
import Logger from 'services/Logger'
import {GLOBAL_PREFETCH_KEYS} from 'prefetch/prefetch.config'
import {queryClient} from 'services/queryClient'
import {DownloadPollingProvider} from 'features/CASB/context/DownloadPollingContext'
import {AllRoutes} from './routes/Routes'
import {ErrorBoundary} from './ErrorPage'
import {useGlobalPrefetch} from '../prefetch'
import {SuspenseFallback} from './SuspenseFallback'
import DLPComingSoonBackground from 'assets/images/dlp-coming-soon-bg.png'
import {useEntitlementsQuery} from 'features/Entitlements/hooks'

const hideTopNavRoutes = [OIDCRouteEnum.BASE as string]

const Main = () => {
  const {isLeftNavHidden} = useNativeInfo()
  const {isBasePolicyToBeColored} = usePolicy()
  const {isSnackbarActionAvailable, isUserLoggedIn} = useActions()
  // Fetch entitlements of the tenant
  const {isLoading: isEntitlementsLoading} = useEntitlementsQuery({
    enabled: isUserLoggedIn,
  })
  const {pathname} = useLocation()
  const isCommonFooterUsed = useCommonFooter()
  const {isDLPComingSoonScreenEnabled} = useFeatureFlag()

  const refetchQueriesOnWindowFocus = (): void => {
    GLOBAL_PREFETCH_KEYS.forEach(key => {
      queryClient.refetchQueries([key])
    })
  }

  // Effect for refetching all global queries on window refocus.
  useEffect(() => {
    window.addEventListener('focus', refetchQueriesOnWindowFocus)
    return () => {
      window.removeEventListener('focus', refetchQueriesOnWindowFocus)
    }
  }, [])

  return (
    <MainLayout
      classes={isBasePolicyToBeColored ? 'bg-dope-teal-100' : 'bg-dope-black'}
    >
      <RenderIf isTrue={isUserLoggedIn && !hideTopNavRoutes.includes(pathname)}>
        <TopNavigation />
      </RenderIf>
      <MainContainer
        hasCommonFooterUsed={isCommonFooterUsed}
        isSnackbarAvailable={isSnackbarActionAvailable}
        className="grid grid-cols-14 h-full"
      >
        <RenderIf
          isTrue={
            pathname === PolicyRoutesEnum.DLP &&
            isDLPComingSoonScreenEnabled &&
            !isEntitlementsLoading
          }
        >
          <img
            src={DLPComingSoonBackground}
            className="absolute top-0 left-0 z-1 w-full h-full pointer-events-none"
            alt="DLP Coming Soon Gradient"
          />
        </RenderIf>
        <div
          className={classNames('col-span-2', {
            hidden: isLeftNavHidden,
          })}
        >
          <LeftNavigation />
        </div>
        <div
          className={classNames('overflow-hidden', {
            'col-span-14': isLeftNavHidden,
            'col-span-12 border-l-0.5 border-gray-400': !isLeftNavHidden,
          })}
        >
          <Suspense fallback={<SuspenseFallback />}>
            <AllRoutes />
          </Suspense>
        </div>
      </MainContainer>
      <PageFooter />
      <TrialFooter />
    </MainLayout>
  )
}

export default function App(): ReactElement {
  const user = useSelector(selectUser)

  useEffect(() => {
    if (user?.emailId) {
      Logger.SetUserLogScope({
        emailId: user.emailId,
        name: user.name,
        primaryDomain: user.primaryDomain,
      })
    }
  }, [user])

  useGlobalPrefetch({
    shouldRun: Boolean(user?.current?.token),
    authProvider: user?.authProvider as AuthProviderEnum,
  })

  return (
    <ErrorBoundary>
      <ToastContainer
        position={toast.POSITION.BOTTOM_RIGHT}
        hideProgressBar
        transition={Bounce}
      />
      <SnapshotProvider>
        <DownloadPollingProvider>
          <Main />
        </DownloadPollingProvider>
      </SnapshotProvider>
    </ErrorBoundary>
  )
}
